/** Sell threshold shadow analysis for loss structure. */
import { SellAnalysisBlock } from '../../services/lossStructureApi';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const BIN_LABELS: Record<string, string> = {
  'le_-0.75': '≤ -0.75',
  '-0.75_-0.6': '-0.75~-0.60',
  '-0.6_-0.45': '-0.60~-0.45',
  '-0.45_-0.25': '-0.45~-0.25',
  '-0.25_0': '-0.25~0',
  ge_0: '≥ 0',
};

interface Props {
  analysis: SellAnalysisBlock;
}

export default function LossSellAnalysis({ analysis }: Props) {
  const { effective, sample } = analysis;
  return (
    <section className="loss-section loss-section-wide">
      <div className="loss-section-heading">
        <div>
          <h3>Sell Threshold 민감도</h3>
          <p className="loss-subnote">shadow · 관측 전용 · 전략값/주문 로직 변경 없음</p>
        </div>
        <span className="loss-pill">DB 기준 {effective.sell_threshold.toFixed(2)}</span>
      </div>

      <div className="loss-sell-summary">
        <div><span>전체 결정</span><strong>{sample.all_decisions}</strong></div>
        <div><span>SELL 가능 맥락</span><strong>{sample.sell_eligible_decisions}</strong></div>
        <div><span>실제 전략 SELL</span><strong>{sample.actual_strategy_sells}</strong></div>
        <div><span>표본 게이트</span><strong>{sample.sample_gate}</strong></div>
      </div>

      <div className="loss-sell-grid">
        <AnalysisTable title="weighted_total 분포" rows={analysis.distribution} />
        <SensitivityTable rows={analysis.sensitivity} />
        <OutcomeTable rows={analysis.actual_outcomes} />
      </div>

      <ul className="loss-notes">
        {analysis.notes.map((note) => <li key={note}>{note}</li>)}
      </ul>
    </section>
  );
}

function AnalysisTable({ title, rows }: { title: string; rows: SellAnalysisBlock['distribution'] }) {
  return (
    <div>
      <h4>{title}</h4>
      <table className="loss-table">
        <thead><tr><th>구간</th><th>전체</th><th>가능</th><th>HOLD</th><th>SELL</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bin}>
              <td>{BIN_LABELS[r.bin] || r.bin}</td>
              <td>{r.all_count}</td>
              <td>{r.eligible_count}</td>
              <td>{r.hold_count}</td>
              <td>{r.sell_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SensitivityTable({ rows }: { rows: SellAnalysisBlock['sensitivity'] }) {
  return (
    <div>
      <h4>임계값 후보별 빈도</h4>
      <table className="loss-table">
        <thead><tr><th>후보</th><th>would SELL</th><th>Δ</th><th>보류</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.threshold} className={r.is_effective ? 'loss-row-active' : ''}>
              <td>{r.threshold.toFixed(2)}{r.is_effective ? ' 현재' : ''}</td>
              <td>{r.would_sell_count}</td>
              <td>{r.delta_vs_effective > 0 ? '+' : ''}{r.delta_vs_effective}</td>
              <td>{r.prevented_sells || r.swing_holds}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutcomeTable({ rows }: { rows: SellAnalysisBlock['actual_outcomes'] }) {
  return (
    <div>
      <h4>실제 STRATEGY_SELL 결과</h4>
      <table className="loss-table">
        <thead><tr><th>구간</th><th>n</th><th>평균%</th><th>합계</th><th>승률</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bin} className={r.count > 0 && !r.sample_ok ? 'loss-row-muted' : ''}>
              <td>{BIN_LABELS[r.bin] || r.bin}</td>
              <td>{r.count}{r.count > 0 && !r.sample_ok ? ' 표본부족' : ''}</td>
              <td className={toneClass(r.avg_profit_percent)}>{r.avg_profit_percent.toFixed(2)}%</td>
              <td className={toneClass(r.sum_amount)}>{money.format(r.sum_amount)}</td>
              <td>{r.win_rate.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function toneClass(value: number): string {
  if (value > 0) return 'loss-cell-good';
  if (value < 0) return 'loss-cell-bad';
  return '';
}
